import CheckIcon from '@mui/icons-material/Check';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  addDaysToIsoDay,
  availableWarningDayOptions,
  DEADLINE_MAX_WARNING_DAYS_COUNT,
  isIsoDay,
} from '@prospero/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { togglePillGroupSx } from '../../../components/toggle-pill-group-sx';
import { getLocalTodayIso } from '../../../config/timezone';
import { formatIsoDay } from './date-only';
import { formatWarningDaysList, warningDaysPluralCount } from './warning-days-summary';

const WARNING_DAY_OPTIONS = [90, 60, 45, 30, 15, 7, 3, 1];
const PILL_GROUP_SX = togglePillGroupSx({ fillRow: false });

interface DeadlineWarningDaysFieldProps {
  value: number[];
  onChange: (days: number[]) => void;
  hasError?: boolean;
  /** Distingue gli id quando due form sono montati insieme. */
  idPrefix: string;
  /** Namespace delle chiavi `warningDays*`: il consulente ha le sue traduzioni. */
  translationPrefix?: string;
  /** Scadenza (YYYY-MM-DD): i preavvisi offerti cadono tra oggi e questa data. Assente = nessun limite. */
  dueAt?: string | null;
}

function haveSameDays(first: readonly number[], second: readonly number[]): boolean {
  return first.length === second.length && first.every((days, index) => days === second[index]);
}

/**
 * Con una data, la scelta tiene solo i preavvisi raggiungibili; se ne resta uno solo è scelto da sé,
 * perché non c'è nulla da decidere. Restituisce se qualche preavviso è stato tolto.
 */
function useReachableWarningDays(
  value: number[],
  reachableOptions: number[],
  isRangeConstrained: boolean,
  onChange: (days: number[]) => void,
): boolean {
  const [hasPrunedSelection, setHasPrunedSelection] = useState(false);
  useEffect(() => {
    if (!isRangeConstrained) {
      return;
    }
    const reachableSelection = value.filter((days) => reachableOptions.includes(days));
    if (reachableSelection.length !== value.length) {
      setHasPrunedSelection(true);
    }
    const nextSelection =
      reachableOptions.length === 1 ? [...reachableOptions] : reachableSelection;
    if (!haveSameDays(nextSelection, value)) {
      onChange(nextSelection);
    }
  }, [value, reachableOptions, isRangeConstrained, onChange]);
  return hasPrunedSelection;
}

function useWarningDaysSummary(value: number[], translationPrefix: string): string {
  const { t, i18n } = useTranslation();
  if (value.length === 0) {
    return t(`${translationPrefix}.warningDaysHelper`);
  }
  return t(`${translationPrefix}.warningDaysSummary`, {
    count: warningDaysPluralCount(value),
    days: formatWarningDaysList(value, i18n.resolvedLanguage ?? i18n.language),
  });
}

type NoticeLine =
  | { kind: 'firstWarningOn'; day: string }
  | { kind: 'firstWarningToday' }
  /** L'avviso del giorno di scadenza parte sempre, anche senza preavvisi. */
  | { kind: 'noWarningsDueDayOnly' };

/**
 * Cosa succede dopo, in una riga: il giorno del preavviso più lungo, oppure che senza preavvisi
 * arriva solo l'avviso del giorno di scadenza. Nulla con la scadenza oggi o già passata.
 */
function resolveNoticeLine(
  value: readonly number[],
  dueAt: string | null | undefined,
  todayIso: string,
): NoticeLine | null {
  const hasDate = Boolean(dueAt) && isIsoDay(dueAt ?? '');
  if (hasDate && (dueAt ?? '') <= todayIso) {
    return null;
  }
  if (value.length === 0) {
    return { kind: 'noWarningsDueDayOnly' };
  }
  if (!hasDate) {
    return null;
  }
  const firstWarningDay = addDaysToIsoDay(dueAt ?? '', -Math.max(...value));
  if (firstWarningDay < todayIso) {
    return null;
  }
  return firstWarningDay === todayIso
    ? { kind: 'firstWarningToday' }
    : { kind: 'firstWarningOn', day: firstWarningDay };
}

interface NoticeLineTextProps {
  line: NoticeLine | null;
  translationPrefix: string;
  testId: string;
}

/** Il consulente ha i suoi testi (l'avviso va al cliente), il titolare i suoi. */
function NoticeLineText({ line, translationPrefix, testId }: NoticeLineTextProps) {
  const { t } = useTranslation();
  if (line === null) {
    return null;
  }
  return (
    <FormHelperText data-testid={testId} sx={{ mx: 0, mt: 0 }}>
      {t(`${translationPrefix}.${line.kind}`, {
        ...(line.kind === 'firstWarningOn' && { date: formatIsoDay(line.day) }),
      })}
    </FormHelperText>
  );
}

interface WarningDayPillsProps {
  value: number[];
  reachableOptions: number[];
  onChange: (days: number[]) => void;
  idPrefix: string;
  translationPrefix: string;
}

/** Tutte le opzioni a vista, la spunta su quelle scelte; al quarto preavviso le altre si fermano. */
function WarningDayPills({
  value,
  reachableOptions,
  onChange,
  idPrefix,
  translationPrefix,
}: WarningDayPillsProps) {
  const { t } = useTranslation();
  const isFull = value.length >= DEADLINE_MAX_WARNING_DAYS_COUNT;
  return (
    <ToggleButtonGroup
      value={value}
      onChange={(_event, chosenDays: number[]) => {
        if (chosenDays.length <= DEADLINE_MAX_WARNING_DAYS_COUNT) {
          onChange([...chosenDays].sort((first, second) => second - first));
        }
      }}
      sx={PILL_GROUP_SX}
    >
      {reachableOptions.map((days) => {
        const isSelected = value.includes(days);
        return (
          <ToggleButton
            key={days}
            value={days}
            disabled={isFull && !isSelected}
            data-testid={`${idPrefix}-warning-day-${days}`}
          >
            {isSelected && <CheckIcon aria-hidden fontSize="small" sx={{ mr: 0.75 }} />}
            {t(`${translationPrefix}.warningDaysOption`, { count: days })}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}

export function DeadlineWarningDaysField({
  value,
  onChange,
  hasError = false,
  idPrefix,
  translationPrefix = 'documents.deadlines.create',
  dueAt,
}: DeadlineWarningDaysFieldProps) {
  const { t } = useTranslation();
  const labelId = `${idPrefix}-warning-days-label`;
  const summaryId = `${idPrefix}-warning-days-summary`;
  const todayIso = getLocalTodayIso();
  const reachableOptions = availableWarningDayOptions(WARNING_DAY_OPTIONS, dueAt, todayIso);
  const hasPrunedSelection = useReachableWarningDays(
    value,
    reachableOptions,
    Boolean(dueAt) && isIsoDay(dueAt ?? ''),
    onChange,
  );
  const summary = useWarningDaysSummary(value, translationPrefix);
  const showsMaxReached =
    value.length >= DEADLINE_MAX_WARNING_DAYS_COUNT &&
    reachableOptions.length > DEADLINE_MAX_WARNING_DAYS_COUNT;

  return (
    // Un solo gruppo con nome per lettori di schermo e test: il fieldset, descritto dal riepilogo.
    <FormControl
      component="fieldset"
      aria-labelledby={labelId}
      aria-describedby={reachableOptions.length > 1 ? summaryId : undefined}
      error={hasError}
      fullWidth
      sx={{ gap: 1 }}
    >
      <FormLabel
        component="legend"
        id={labelId}
        sx={{
          typography: 'body2',
          fontWeight: 600,
          color: 'text.primary',
          '&.Mui-focused': { color: 'text.primary' },
        }}
      >
        {t(`${translationPrefix}.warningDaysLabel`)}
      </FormLabel>
      {reachableOptions.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t(`${translationPrefix}.warningDaysTodayHelper`)}
        </Typography>
      )}
      {reachableOptions.length === 1 && (
        <Box
          role="status"
          data-testid={`${idPrefix}-warning-days-auto`}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <NotificationsActiveOutlinedIcon
            aria-hidden
            fontSize="small"
            sx={{ color: 'text.secondary' }}
          />
          <Typography variant="body2">{summary}</Typography>
        </Box>
      )}
      {reachableOptions.length > 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <WarningDayPills
            value={value}
            reachableOptions={reachableOptions}
            onChange={onChange}
            idPrefix={idPrefix}
            translationPrefix={translationPrefix}
          />
          <FormHelperText id={summaryId} role="status" sx={{ mx: 0 }}>
            {summary}
          </FormHelperText>
          {showsMaxReached && (
            <FormHelperText sx={{ mx: 0, mt: 0 }}>
              {t(`${translationPrefix}.warningDaysMaxReached`)}
            </FormHelperText>
          )}
        </Box>
      )}
      <NoticeLineText
        line={resolveNoticeLine(value, dueAt, todayIso)}
        translationPrefix={translationPrefix}
        testId={`${idPrefix}-warning-days-first`}
      />
      {hasPrunedSelection && reachableOptions.length > 0 && (
        <FormHelperText sx={{ mx: 0, mt: 0, color: 'info.dark' }}>
          {t(`${translationPrefix}.warningDaysPrunedNotice`)}
        </FormHelperText>
      )}
    </FormControl>
  );
}
